import React, { useEffect, useMemo, useState } from 'react';
import { Table, Badge } from 'react-bootstrap';
import PhoenixDocCard from 'components/base/PhoenixDocCard';
import { useSelector } from 'react-redux';
import { selectProjectResults } from '../data-access/project.reducer';
import { pickLatestGovernanceRun } from '../../shared/governanceResultsAccess';

interface RegulationMatch {
  regulation?: string;
  article?: string;
  obligation?: string;
  distance?: number;
  reason?: string;
}

interface ExternalComplianceItem {
  dimension?: string;
  issue_type?: string;
  feature?: string;
  evidence?: string;
  status?: string;
  severity?: string; // new backend
  matches?: RegulationMatch[];
  reasoning?: string; // new backend
  reason?: string; // old backend fallback
}

interface ExternalComplianceResult {
  result_id?: string;
  title?: string;
  timestamp?: string | { $date?: string };
  userId?: string;
  projectId?: string;
  environment?: string;
  overall_external_status?: string;
  external_compliance?: ExternalComplianceItem[];
  total_events?: number;
  fail_count?: number;
  not_covered_count?: number;
  chart_data?: {
    summary?: {
      total_events?: number;
      fail_count?: number;
      not_covered_count?: number;
    };
  };
}

interface ExternalComplianceTabProps {
  projectId: string;
  userId: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ExternalComplianceTab = (_props: ExternalComplianceTabProps) => {
  const projectResults = useSelector(selectProjectResults);
  const [data, setData] = useState<ExternalComplianceResult | null>(null);

  const externalComplianceData = useMemo(
    () => pickLatestGovernanceRun<ExternalComplianceResult>(projectResults, 'external_compliance'),
    [projectResults]
  );

  useEffect(() => {
    setData(externalComplianceData || null);
  }, [externalComplianceData]);

  const complianceItems: ExternalComplianceItem[] = Array.isArray(data?.external_compliance)
    ? data!.external_compliance!
    : [];

  const formatDate = (timestamp?: string | { $date?: string }) => {
    if (!timestamp) return '—';
    const raw = typeof timestamp === 'object' ? timestamp.$date : timestamp;
    if (!raw) return '—';

    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? raw : parsed.toLocaleString();
  };

  const getOverallStatusBadge = (status?: string) => {
    switch ((status || '').toUpperCase()) {
      case 'FAIL':
        return <Badge bg="danger">FAIL</Badge>;
      case 'OK':
        return <Badge bg="success">OK</Badge>;
      case 'NO_EVENTS':
        return <Badge bg="dark">NO EVENTS</Badge>;
      case 'NO_POLICY':
        return (
          <Badge bg="warning" text="dark">
            NO POLICY
          </Badge>
        );
      default:
        return <Badge bg="secondary">{status || '—'}</Badge>;
    }
  };

  const getStatusBadge = (status?: string) => {
    switch ((status || '').toUpperCase()) {
      case 'FAIL':
        return <Badge bg="danger">FAIL</Badge>;
      case 'PASS':
      case 'OK':
        return <Badge bg="success">{status?.toUpperCase()}</Badge>;
      case 'NOT_COVERED':
        return <Badge bg="secondary">NOT_COVERED</Badge>;
      default:
        return <Badge bg="secondary">{status || '—'}</Badge>;
    }
  };

  const getDerivedSeverity = (item: ExternalComplianceItem) => {
    if (item.severity) return item.severity.toUpperCase();

    const status = (item.status || '').toUpperCase();
    const dimension = (item.dimension || '').toUpperCase();
    const issueType = (item.issue_type || '').toUpperCase();

    if (
      status === 'FAIL' &&
      (dimension.includes('BIAS') ||
        dimension.includes('FAIRNESS') ||
        dimension.includes('SAFETY') ||
        dimension.includes('ABUSE') ||
        dimension.includes('PROMPT') ||
        issueType.includes('PROMPT'))
    ) {
      return 'HIGH';
    }

    if (status === 'FAIL') return 'MEDIUM';
    if (status === 'NOT_COVERED') return 'LOW';
    return 'LOW';
  };

  const getSeverityBadge = (severity?: string) => {
    switch ((severity || '').toUpperCase()) {
      case 'HIGH':
        return <Badge bg="danger">HIGH</Badge>;
      case 'MEDIUM':
        return (
          <Badge bg="warning" text="dark">
            MEDIUM
          </Badge>
        );
      case 'LOW':
        return <Badge bg="success">LOW</Badge>;
      default:
        return <Badge bg="secondary">{severity || '—'}</Badge>;
    }
  };

  const getDerivedReasoning = (item: ExternalComplianceItem) => {
    if (item.reasoning) return item.reasoning;
    if (item.reason) return item.reason;

    const firstMatch = item.matches?.[0];
    if (firstMatch) {
      return `Matched with ${firstMatch.regulation || 'external regulation'}${
        firstMatch.article ? ` ${firstMatch.article}` : ''
      }.`;
    }

    if ((item.status || '').toUpperCase() === 'NOT_COVERED') {
      return 'No strong regulatory match found for this issue.';
    }

    return 'No detailed reasoning available.';
  };

  const renderClauseCell = (item: ExternalComplianceItem) => {
    const firstMatch = item.matches?.[0];

    if (!firstMatch) {
      return <span className="text-muted small">No matching external clause</span>;
    }

    const similarity =
      typeof firstMatch.distance === 'number' ? `${(Math.max(0, 1 - firstMatch.distance) * 100).toFixed(1)}%` : null;

    return (
      <div>
        <div className="small">
          <strong>{firstMatch.regulation || 'Unknown Regulation'}</strong>
          {firstMatch.article ? `: ${firstMatch.article}` : ''}
        </div>

        {firstMatch.obligation && (
          <div className="small text-muted mt-2">
            {firstMatch.obligation.length > 140
              ? `${firstMatch.obligation.substring(0, 140)}...`
              : firstMatch.obligation}
          </div>
        )}

        {similarity && (
          <div className="mt-2">
            <Badge bg="dark">Similarity: {similarity}</Badge>
          </div>
        )}
      </div>
    );
  };

  const totalEvents = data?.total_events ?? data?.chart_data?.summary?.total_events ?? complianceItems.length;

  const failCount =
    data?.fail_count ??
    data?.chart_data?.summary?.fail_count ??
    complianceItems.filter(item => (item.status || '').toUpperCase() === 'FAIL').length;

  const notCoveredCount =
    data?.not_covered_count ??
    data?.chart_data?.summary?.not_covered_count ??
    complianceItems.filter(item => (item.status || '').toUpperCase() === 'NOT_COVERED').length;

  const isNoEvents = (data?.overall_external_status || '').toUpperCase() === 'NO_EVENTS';

  return (
    <div className="mb-4">
      <PhoenixDocCard className="mb-4">
        <PhoenixDocCard.Header title="External Compliance Analysis">
          <p className="text-muted mb-0">External regulatory compliance analysis based on project results.</p>
        </PhoenixDocCard.Header>

        <PhoenixDocCard.Body>
          {data ? (
            <div className="mt-4">
              <div className="d-flex align-items-center gap-5 mb-4 p-3 bg-light rounded border flex-wrap">
                <div>
                  <h5 className="mb-2">Overall Status</h5>
                  {getOverallStatusBadge(data.overall_external_status)}
                </div>

                <div>
                  <h5 className="mb-2">Title</h5>
                  <span className="fw-bold text-primary">{data.title || '—'}</span>
                </div>

                <div>
                  <h5 className="mb-2">Generated At</h5>
                  <span className="text-muted">{formatDate(data.timestamp)}</span>
                </div>

                <div>
                  <h5 className="mb-2">Total Events</h5>
                  <span className="fw-semibold">{totalEvents}</span>
                </div>

                <div>
                  <h5 className="mb-2">Fail Count</h5>
                  <span className="fw-semibold text-danger">{failCount}</span>
                </div>

                <div>
                  <h5 className="mb-2">Not Covered</h5>
                  <span className="fw-semibold text-secondary">{notCoveredCount}</span>
                </div>
              </div>

              <h4 className="mb-3">Compliance Issues</h4>

              <div className="table-responsive">
                <Table bordered hover className="align-middle">
                  <thead className="bg-light">
                    <tr>
                      <th style={{ width: '15%' }}>Dimension</th>
                      <th style={{ width: '15%' }}>Issue Type</th>
                      <th style={{ width: '10%' }}>Severity</th>
                      <th style={{ width: '10%' }}>Status</th>
                      <th style={{ width: '15%' }}>Feature/Model</th>
                      <th style={{ width: '15%' }}>Evidence</th>
                      <th style={{ width: '20%' }}>External Clause</th>
                    </tr>
                  </thead>

                  <tbody>
                    {complianceItems.length > 0 ? (
                      complianceItems.map((item, idx) => {
                        const severity = getDerivedSeverity(item);

                        return (
                          <tr key={idx}>
                            <td className="fw-semibold">{item.dimension || '—'}</td>
                            <td>{item.issue_type || '—'}</td>
                            <td>{getSeverityBadge(severity)}</td>
                            <td>{getStatusBadge(item.status)}</td>
                            <td>
                              <div className="fw-semibold">{item.feature || '—'}</div>
                              <small className="text-muted">External Compliance</small>
                            </td>
                            <td>
                              <small className="text-muted">{item.evidence || '—'}</small>
                            </td>
                            <td>{renderClauseCell(item)}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={7} className="text-center py-4">
                          {isNoEvents ? (
                            <div>
                              <div className="fw-semibold text-muted">No external compliance events were detected.</div>
                              <div className="small text-muted mt-1">
                                The analysis completed, but no fairness, bias, abuse, prompt injection, governance, or
                                oversight issues were extracted for this project.
                              </div>
                            </div>
                          ) : (
                            <span className="text-muted">No compliance issues found.</span>
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              <div className="mt-5">
                <h4 className="mb-3">Detailed Reasons</h4>

                {complianceItems.length > 0 ? (
                  complianceItems.map((item, idx) => {
                    const severity = getDerivedSeverity(item);
                    const reasoning = getDerivedReasoning(item);

                    return (
                      <div key={idx} className="p-3 bg-light rounded border mb-3">
                        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                          <span className="fw-bold">{item.issue_type || 'Issue'}</span>
                          {getSeverityBadge(severity)}
                          {getStatusBadge(item.status)}
                        </div>

                        <div className="small mb-2">
                          <strong>Dimension:</strong> {item.dimension || '—'}
                        </div>

                        <div className="small mb-2">
                          <strong>Feature/Model:</strong> {item.feature || '—'}
                        </div>

                        <div className="small mb-2">
                          <strong>Evidence:</strong> {item.evidence || '—'}
                        </div>

                        <div className="small mb-2">
                          <strong>Reasoning:</strong> {reasoning}
                        </div>

                        {item.matches && item.matches.length > 0 && (
                          <div className="mt-3">
                            <strong className="small">Matched External Clauses:</strong>
                            <div className="mt-2">
                              {item.matches.map((match, matchIdx) => (
                                <div key={matchIdx} className="border rounded p-2 bg-white mb-2">
                                  <div className="fw-semibold">
                                    {match.regulation || 'Unknown Regulation'}
                                    {match.article ? ` — ${match.article}` : ''}
                                  </div>

                                  {match.obligation && <div className="small text-muted mt-1">{match.obligation}</div>}

                                  {typeof match.distance === 'number' && (
                                    <div className="small mt-1">
                                      Similarity: {(Math.max(0, 1 - match.distance) * 100).toFixed(1)}%
                                    </div>
                                  )}

                                  {match.reason && <div className="small text-muted mt-1">{match.reason}</div>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="p-3 bg-light rounded border text-muted">
                    {isNoEvents
                      ? 'No detailed reasons are available because no external compliance events were extracted from the project results.'
                      : 'No detailed reasons available.'}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-5 text-muted">
              <h5>No Analysis Data</h5>
              <p>No External Compliance analysis data available for this project.</p>
            </div>
          )}
        </PhoenixDocCard.Body>
      </PhoenixDocCard>
    </div>
  );
};

export default ExternalComplianceTab;
